import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Mail, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage, useApi } from "@/lib/api";
import { SUB_LABELS } from "@/lib/format";
import type { Organization } from "@/lib/types";
import { Loading, PageHeader } from "@/components/app/PageHeader";

interface Stats { organizations: number; suspended: number; screens: number; users: number }

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const orgs = useApi<Organization[]>("/admin/organizations");
  const stats = useApi<Stats>("/admin/stats");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", plan: "Essentiel", screen_quota: 5 });
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [sending, setSending] = useState(false);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post<{ status: string; error: string | null }>("/admin/email/test", { to: mailTo });
      if (data.status === "sent") { toast.success(`Courriel de test envoyé à ${mailTo}`); setMailOpen(false); }
      else toast.error(data.status === "skipped" ? "Domaine de démonstration : envoi ignoré" : `Échec SMTP : ${data.error}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setSending(false); }
  };

  const create = async () => {
    try {
      const { data } = await api.post<Organization>("/admin/organizations", { ...form, screen_quota: Number(form.screen_quota) });
      toast.success("Organisation créée");
      navigate(`/admin/organisations/${data.id}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Super-admin" title="Organisations" description="Abonnements et quotas d'écrans gérés manuellement (paiement en ligne prévu après la v1)."
        actions={<>
          <Button data-testid="btn-test-email" variant="outline" onClick={() => setMailOpen(true)} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Mail className="mr-1 h-4 w-4" />Courriel test</Button>
          <Button data-testid="btn-create-org" onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Nouvelle organisation</Button>
        </>} />
      {stats.data && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[["Organisations", stats.data.organizations, "stat-orgs"], ["Suspendues", stats.data.suspended, "stat-suspended"], ["Écrans actifs", stats.data.screens, "stat-screens"], ["Utilisateurs", stats.data.users, "stat-users"]].map(([l, v, t]) => (
            <div key={t as string} className="panel p-5"><p className="eyebrow">{l}</p><p data-testid={t as string} className="mt-3 font-display text-4xl font-semibold text-slate-100">{v}</p></div>
          ))}
        </div>
      )}
      {orgs.loading && !orgs.data ? <Loading /> : (
        <div className="panel overflow-x-auto">
          <Table data-testid="orgs-table">
            <TableHeader><TableRow className="border-slate-800 hover:bg-transparent"><TableHead>Organisation</TableHead><TableHead>Forfait</TableHead><TableHead>Écrans utilisés / quota</TableHead><TableHead>Abonnement</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {(orgs.data || []).map((o) => (
                <TableRow key={o.id} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell><Link to={`/admin/organisations/${o.id}`} data-testid={`org-row-link-${o.id}`} className="flex items-center gap-2 font-medium text-slate-100 hover:text-blue-300"><Building2 className="h-4 w-4 text-cyan-400" />{o.name}</Link></TableCell>
                  <TableCell className="text-slate-300">{o.plan}</TableCell>
                  <TableCell className="font-mono text-slate-300" data-testid={`org-quota-${o.id}`}>{o.screens_used} / {o.screen_quota}</TableCell>
                  <TableCell className="text-slate-400">{SUB_LABELS[o.subscription_status] || o.subscription_status}</TableCell>
                  <TableCell><span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${o.status === "active" ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-rose-500/30 bg-rose-500/15 text-rose-400"}`}>{o.status === "active" ? "Active" : "Suspendue"}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent data-testid="test-email-dialog" className="border-slate-800 bg-slate-900">
          <DialogHeader><DialogTitle>Tester l'envoi de courriels (SMTP)</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Expéditeur : info@innova-sign.ca</p>
          <Input type="email" data-testid="test-email-input" value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="destinataire@exemple.ca" className="border-slate-700 bg-slate-950/60" />
          <Button data-testid="test-email-submit" disabled={!mailTo.includes("@") || sending} onClick={sendTest} className="bg-blue-600 text-white hover:bg-blue-500">{sending ? "Envoi…" : "Envoyer"}</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900">
          <DialogHeader><DialogTitle>Nouvelle organisation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nom</Label><Input data-testid="org-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Forfait</Label><Input data-testid="org-plan-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
              <div className="space-y-2"><Label>Quota d'écrans</Label><Input type="number" min={0} data-testid="org-quota-input" value={form.screen_quota} onChange={(e) => setForm({ ...form, screen_quota: Number(e.target.value) })} className="border-slate-700 bg-slate-950/60" /></div>
            </div>
            <Button data-testid="btn-submit-org" disabled={form.name.trim().length < 2} onClick={create} className="w-full bg-blue-600 text-white hover:bg-blue-500">Créer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
